import { Reducer } from "redux";

import {
  BaseReduxPubsubSlice,
  SliceOptionsType,
  getPublishableAction,
} from "./base_redux_pubsub_slice";

import {
  AckStateType,
  ActionDirectlyFromClientType,
  ClassroomActionType,
  ClassroomActionTypeType,
  EnhancedReducerActionType,
  PublishableActionType,
} from "shared/types";

const IMPORTANT_ACTIONS_TO_REPLAY: ClassroomActionTypeType[] = [
  "classroom/join",
  "classroom/leave",
  "classroom/setUserConnectionQuality",
  "classroom/setUserConnectionState",
  "classroom/setUserStreamState",
  "classroom/setUserMicError",
  "classroom/setUserCameraError",
  "classroom/setUserIsPerformingAvCheck",
];

// AckReduxPubsubSlice is used for slices that need to be acknowledged by the server.
// For example, the classroom slice is an AckReduxPubsubSlice because it affects the UI
// (the server accepts or rejects the action depending on the user's permissions)
// and needs to be synced across clients.
//
// The main difference between AckReduxPubsubSlice and ReduxPubsubSlice is that the
// reducer for AckReduxPubsubSlice has a local (client) state and a server state. The local state
// is updated immediately when an action is dispatched, and the server state is updated
// when the server acknowledges the action.
//
// The reducer for AckReduxPubsubSlice also has a pendingActions queue. When an action
// is dispatched, it is added to the pendingActions queue. When the server acknowledges
// the action, it is removed from the pendingActions queue. If the server rejects the
// action, it is removed from the pendingActions queue and undone on the local state.
export class AckReduxPubsubSlice<StateType> extends BaseReduxPubsubSlice<StateType> {
  reducer: Reducer<AckStateType<StateType>, EnhancedReducerActionType>;

  constructor(options: SliceOptionsType<StateType>) {
    super(options);
    this.reducer = this.getReducer();
  }

  validate() {
    if (!this.channel) {
      throw new Error("Cannot create this type of slice without a channel");
    }
    if (!this.flightAuthToken) {
      throw new Error("Cannot create this type of slice without a flightAuthToken");
    }
    super.validate();
  }

  reduceAndPublish(
    state: AckStateType<StateType>,
    action: ActionDirectlyFromClientType | PublishableActionType,
  ): AckStateType<StateType> {
    // 1. The action is reduced against the client version
    // NB: this is the original action without psid,
    // not sure if other reducers should know/care about meta.psid
    const newClient = this.reduce(state.client, action);
    // If the action does not produce any state change, there is no need to publish.
    // Some actions are meant to be published even if they do not modify the
    // state (for example, sendPubsubMessage). For these actions, the forcePublish flag
    // should be set to true.
    if (action.forcePublish !== true && newClient === state.client) {
      return state;
    }

    // 2. Assign a psid for publishing the action
    const publishableAction = getPublishableAction(action, this.userId);

    // 3. The action is published to the server.
    if (this.publishActions) {
      this.socketSend({
        type: "action",
        action: publishableAction,
      });
    }

    // 4. The action is appended to pendingActions
    const newPendingActions = [...state.pendingActions, publishableAction];

    // 5. New state is returned
    return {
      client: newClient,
      server: state.server,
      pendingActions: newPendingActions,
      stalePendingActions: state.stalePendingActions,
    } as AckStateType<StateType>;
  }

  // Enhance the slice reducer, which will be called whenever an action is dispatched.
  // - The reducer will
  //   - apply the action to the local state
  //   - add the action to the pendingActions queue
  //   - publish the action to the server
  //   - wait for the server to acknowledge the action
  //   - if the server rejects the action, remove it from the pendingActions queue and undo it on the local state
  //   - if the server accepts the action, remove it from the pendingActions queue and apply it to the server state
  // - For publishable slices, the reducer will also publish the action to the server.
  // - For syncable slices, the reducer will also reset the local state when receiving a sync message.
  public getReducer(): Reducer<AckStateType<StateType>, EnhancedReducerActionType> {
    this.setUpSocket();

    // Call the reducer with empty action to populate the initial state
    const initialState = {
      client: this.reduce(undefined, { type: "INIT" }),
      server: this.reduce(undefined, { type: "INIT" }),
      pendingActions: [],
      stalePendingActions: [],
    } as AckStateType<StateType>;
    return (
      // eslint-disable-next-line default-param-last
      state = initialState,
      action: EnhancedReducerActionType,
    ) => {
      if (action.meta?.socketType && action.meta.socketType !== this.socket?.socketType) {
        // Dispatched action from the socket is not meant for this slice
        return state;
      }
      if (action.type === "request-sync") {
        if (!this.requestsSync) {
          return state;
        }
        const { pendingActions, stalePendingActions } = state;
        const newStalePendingActions = [...stalePendingActions, ...pendingActions];
        AckReduxPubsubSlice.log(
          "Sending request-sync message, clearing pendingActions. Stale pending actions:",
          stalePendingActions,
        );
        const { type, id } = action;
        this.socketSend({ type, id });
        return {
          client: state.client,
          server: state.server,
          pendingActions: [],
          stalePendingActions: newStalePendingActions,
        } as AckStateType<StateType>;
      }
      if (action.type === "sync") {
        if (!this.requestsSync) {
          return state;
        }
        const { pendingActions, stalePendingActions } = state;
        const newStalePendingActions = [...stalePendingActions, ...pendingActions];
        AckReduxPubsubSlice.log(
          "Received sync message, updating state and clearing pending actions. Stale pending actions:",
          stalePendingActions,
        );

        // Replay important actions that were not acknowledged before the sync started
        let newState = {
          client: action.state as StateType,
          server: action.state as StateType,
          pendingActions: [],
          stalePendingActions: newStalePendingActions,
        } as AckStateType<StateType>;
        stalePendingActions.forEach((pendingAction) => {
          if (IMPORTANT_ACTIONS_TO_REPLAY.includes((pendingAction as ClassroomActionType).type)) {
            AckReduxPubsubSlice.log("Replaying important action:", pendingAction);
            newState = this.reduceAndPublish(newState, pendingAction);
          }
        });

        return newState;
      }
      if (action.type === "ack") {
        // Server acknowledged the corresponding message, either accepting or rejecting it.
        const incomingPsid = action.psid as string;
        const firstPendingActionPsid = (state.pendingActions[0]?.meta?.psid || "") as string;

        if (incomingPsid !== firstPendingActionPsid) {
          if (state.stalePendingActions.find((a) => a.meta?.psid === incomingPsid)) {
            AckReduxPubsubSlice.warn(
              "Received ACK for a stale pending action: ignoring ACK. Action: ",
              action,
            );
            const { client, server, pendingActions, stalePendingActions } = state;
            const newStalePendingActions = stalePendingActions.filter(
              (a) => a.meta?.psid !== incomingPsid,
            );
            return {
              client,
              server,
              pendingActions,
              stalePendingActions: newStalePendingActions,
            };
          }
          throw new Error(
            `Received ACK for action that was not the first pending action and that was not stale. ACK action: ${JSON.stringify(
              action,
            )} and pendingActions: ${JSON.stringify(state.pendingActions)}`,
          );
        }

        // 1. Remove ack'd action from pendingActions
        const [ackedAction, ...newPendingActions] = state.pendingActions;

        // 2. If it was accepted, apply the action to the known server version.
        // (Otherwise, do not apply it, and it will simply get erased out of local history.)
        let newServer: StateType;
        if (action.accepted) {
          newServer = this.reduce(state.server, ackedAction);
          window.pubsubStatsCollector.addRecord(ackedAction);
        } else {
          AckReduxPubsubSlice.error(
            "Action was rejected by the server, unapplying it locally. " +
              "The UI should not allow sending this action given the current user role:",
            ackedAction,
          );
          newServer = state.server;
        }

        // 3. Replay remaining pendingActions atop server version and save as client version:
        let newClient = newServer;
        newPendingActions.forEach((pendingAction) => {
          newClient = this.reduce(newClient, pendingAction);
        });

        // 4. Return new state
        if (state.stalePendingActions.length > 0) {
          AckReduxPubsubSlice.log("Clearing stale pending actions:", state.stalePendingActions);
        }
        return {
          client: newClient,
          server: newServer,
          pendingActions: newPendingActions,
          stalePendingActions: [],
        } as AckStateType<StateType>;
      }

      const origin = action.meta?.origin || "client";

      if (origin === "client") {
        // It is an update from the local client.
        return this.reduceAndPublish(state, action);
      }
      if (origin === "server") {
        // It is an update from another source (another client).
        // 1. Apply the incoming action to the server version
        const newServer = this.reduce(state.server, action);

        // 2. Replay pendingActions atop server version and save as client version:
        let newClient = newServer;
        state.pendingActions.forEach((pendingAction) => {
          newClient = this.reduce(newClient, pendingAction);
        });
        return {
          ...state,
          client: newClient,
          server: newServer,
        } as AckStateType<StateType>;
      }
      return state;
    };
  }
}
